import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, CaretDown, CaretUp, Trash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const SECTIONS = {
  peso: { label: 'Peso', fields: [{ k: 'peso', l: 'Peso (kg)', step: '0.1' }, { k: 'altura', l: 'Altura (cm)', step: '0.1' }] },
  bio: { label: 'Bioimpedância', fields: [
    { k: 'gordura_pct', l: '% Massa gorda', step: '0.1' }, { k: 'massa_gorda', l: 'Massa gorda (kg)', step: '0.1' },
    { k: 'massa_muscular', l: 'Massa muscular (kg)', step: '0.1' }, { k: 'agua_corporal', l: '% Água corporal', step: '0.1' },
    { k: 'massa_ossea', l: 'Massa óssea (kg)', step: '0.1' }, { k: 'gordura_visceral', l: 'Gordura visceral (nível)', step: '1' },
    { k: 'tmb', l: 'TMB (kcal)', step: '1' },
  ]},
};

export default function ComposicaoPage() {
  const confirmDelete = useConfirmDelete();
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [measurements, setMeasurements] = useState({});
  const [expandedSections, setExpandedSections] = useState({ peso: true });
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/weight-records`); setEntries(data); } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));
  const setField = (k, v) => setMeasurements(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!measurements.peso) { toast.error('Indica pelo menos o peso'); return; }
    const payload = Object.fromEntries(Object.entries(measurements).filter(([_, v]) => v !== '' && v !== null && v !== undefined));
    setSaving(true);
    try {
      await axios.post(`${API}/api/weight-records`, { date: new Date().toISOString().split('T')[0], ...payload });
      toast.success('Registo guardado'); setShowForm(false); setMeasurements({}); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };

  const del = async (id) => {
    const e = entries.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo de ${e?.date || ''}${e?.peso ? ` (peso: ${e.peso} kg)` : ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/weight-records/${id}`); toast.success('Eliminado'); fetch(); } catch (e2) { toast.error(e2.response?.data?.detail || 'Erro'); }
  };

  const FIELD_LABELS = { peso: 'Peso (kg)', altura: 'Altura (cm)', gordura_pct: '% Massa gorda', massa_gorda: 'Massa gorda (kg)', massa_muscular: 'Massa muscular (kg)', agua_corporal: '% Água corporal', massa_ossea: 'Massa óssea (kg)', gordura_visceral: 'Gordura visceral', tmb: 'TMB (kcal)' };
  const latest = entries.find(e => !e.is_weekly_average);
  const SOURCE_BADGE = { patient: 'Registado por ti', manual: 'Registado por ti', nutri: 'Registado em consulta', inbody: 'InBody', weekly_average: 'Média semanal' };
  const SOURCE_COLOR = { patient: 'bg-brand-mint/20 text-brand-green', manual: 'bg-brand-mint/20 text-brand-green', nutri: 'bg-brand-bg text-brand-muted', inbody: 'bg-brand-bg text-brand-muted', weekly_average: 'bg-brand-green/10 text-brand-green font-medium' };

  return (
    <div className="fade-in" data-testid="composicao-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Registar medidas</h2>
          <p className="text-xs text-brand-muted mt-0.5">Composição corporal e medições</p>
        </div>
        <button onClick={() => { setMeasurements({}); setExpandedSections({ peso: true }); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-comp-btn">
          <Plus size={16} /> Registar
        </button>
      </div>

      {/* Latest summary */}
      {latest && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {latest.peso && <div className="bg-white border border-brand-border rounded-2xl p-3 text-center"><p className="text-lg font-heading font-semibold text-brand-text">{latest.peso}</p><p className="text-[9px] text-brand-muted">Peso (kg)</p></div>}
          {latest.gordura_pct && <div className="bg-white border border-brand-border rounded-2xl p-3 text-center"><p className="text-lg font-heading font-semibold text-brand-gold-hover">{latest.gordura_pct}%</p><p className="text-[9px] text-brand-muted">Gordura</p></div>}
          {latest.massa_muscular && <div className="bg-white border border-brand-border rounded-2xl p-3 text-center"><p className="text-lg font-heading font-semibold text-brand-green">{latest.massa_muscular}</p><p className="text-[9px] text-brand-muted">Músculo (kg)</p></div>}
        </div>
      )}

      {/* History */}
      <div className="space-y-2">
        {entries.map(e => (
          <div key={e.id} className={`bg-white border rounded-xl p-3 ${e.is_weekly_average ? 'border-brand-green/30' : 'border-brand-border'}`} data-testid={`comp-entry-${e.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-brand-muted">{fmtDatePt(e.date)}</span>
                <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${SOURCE_COLOR[e.source] || ''}`}>{e.is_weekly_average ? 'Média semanal' : (SOURCE_BADGE[e.source] || e.source)}</span>
              </div>
              <div className="flex items-center gap-2">
                {e.peso && <span className="text-sm font-semibold text-brand-text">{e.peso} kg</span>}
                {!e.is_weekly_average && e.source !== 'inbody' && <button onClick={ev => { ev.stopPropagation(); del(e.id); }} className="text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>}
              </div>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[10px]">
              {Object.entries(FIELD_LABELS).filter(([k]) => k !== 'peso' && e[k]).map(([k, l]) => (
                <div key={k}><span className="text-brand-muted">{l}</span><br /><span className="font-medium text-brand-text">{e[k]}</span></div>
              ))}
            </div>
          </div>
        ))}
        {entries.length === 0 && <p className="text-center text-brand-muted py-8 text-sm">Sem registos. Começa a registar as tuas medidas.</p>}
      </div>

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar medidas</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {Object.entries(SECTIONS).map(([key, sec]) => (
              <div key={key} className="border border-brand-border rounded-xl overflow-hidden">
                <button onClick={() => toggleSection(key)} className="w-full flex items-center justify-between p-3 hover:bg-brand-bg/30">
                  <span className="text-xs font-medium text-brand-text">{sec.label}</span>
                  {expandedSections[key] ? <CaretUp size={14} className="text-brand-muted" /> : <CaretDown size={14} className="text-brand-muted" />}
                </button>
                {expandedSections[key] && (
                  <div className="p-3 pt-0 grid grid-cols-2 gap-2">
                    {sec.fields.map(f => (
                      <div key={f.k}>
                        <Label className="text-[9px] text-brand-muted mb-0.5 block">{f.l}</Label>
                        <Input type="number" step={f.step || '0.1'} value={measurements[f.k] || ''} onChange={e => setField(f.k, e.target.value)} className="rounded-lg border-brand-border text-sm h-8" data-testid={`comp-field-${f.k}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <p className="text-[10px] text-brand-placeholder px-1">Queres registar perímetros (cintura, anca, braços...)? Usa a página <a href="/perimetros" className="text-brand-green underline">Os meus perímetros</a>.</p>
            <button onClick={save} disabled={saving} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50 mt-2" data-testid="save-comp-btn">
              {saving ? 'A guardar...' : 'Guardar registo'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

